import { ref, computed, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useNotification } from '@kyvg/vue3-notification';
import { useCookiesStore } from '@/stores/cookies.store';
import { useAppConfigStore } from '@stores/appConfig.store';
const { notify } = useNotification();

export const useShoppingActivationStore = defineStore(
  'shoppingActivation',
  () => {
    const appConfigStore = useAppConfigStore();
    const { applicationSetup, setupLoaded } = storeToRefs(appConfigStore);
    const shoppingActivations = ref([]);
    const currentNotificationIndex = ref(0);
    const hotelId = ref(null);
    const cookiesStore = useCookiesStore();
    const { userCookies } = storeToRefs(cookiesStore);
    const positions = {
      TOP_LEFT: 'top left',
      TOP_RIGHT: 'top right',
      BOTTOM_LEFT: 'bottom left',
      BOTTOM_RIGHT: 'bottom right',
    };

    const setHotelId = (id) => {
      hotelId.value = id;
    };

    const setShoppingActivations = (activations) => {
      if (!activations) return;
      shoppingActivations.value = Array.isArray(activations)
        ? activations
        : [activations];
      currentNotificationIndex.value = 0;
    };

    const cookiePrefix = computed(
      () => `GCShoppingActivations_${hotelId.value}`,
    );

    const notificationPosition = computed(() => {
      const activation =
        shoppingActivations.value[currentNotificationIndex.value];
      if (!activation?.Position) return positions['TOP_RIGHT'];

      return positions[activation.Position] || positions['TOP_RIGHT'];
    });

    const notificationSkin = computed(() => {
      const activation =
        shoppingActivations.value[currentNotificationIndex.value];
      if (!activation?.skin) return 'GREEN';

      return activation.skin;
    });

    const notificationImage = computed(() => {
      const activation =
        shoppingActivations.value[currentNotificationIndex.value];
      if (!activation?.trigger) return '';

      return activation.trigger;
    });

    const showNotifications = () => {
      if (shoppingActivations.value.length === 0) {
        return;
      }
      setTimeout(() => {
        showNextNotification();
      }, 6000);
    };

    const showNextNotification = () => {
      if (!userCookies.value?.functionalityOrPersonalizationCookies) {
        return;
      }

      if (currentNotificationIndex.value >= shoppingActivations.value.length) {
        return;
      }

      const activation =
        shoppingActivations.value[currentNotificationIndex.value];
      const key = `${cookiePrefix.value}_${activation.trigger}`;

      if (getCookieValue(key)) {
        currentNotificationIndex.value++;
        showNextNotification();
        return;
      }
      setCookie(key);
      notify({
        text: activation.text || '',
        duration: 15000,
      });
    };

    const getCookieValue = (key) => {
      const cookie = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${key}=`))
        ?.split('=')[1];
      return cookie ? cookie : null;
    };

    const setCookie = (key) => {
      const expirationDate = new Date();
      expirationDate.setMinutes(expirationDate.getMinutes() + 10);
      document.cookie = `${key}=true; expires=${expirationDate.toUTCString()}; path=/`;
    };

    const destroy = () => {
      currentNotificationIndex.value++;
      setTimeout(() => {
        showNextNotification();
      }, 3000);
    };

    watch(
      () => [
        setupLoaded.value,
        userCookies.value.functionalityOrPersonalizationCookies,
      ],
      () => {
        if (
          setupLoaded.value &&
          userCookies.value.functionalityOrPersonalizationCookies
        ) {
          setHotelId(applicationSetup.value.hotel_id);
          setShoppingActivations(applicationSetup.value.shopping_activation);
          showNotifications();
        }
      },
      { immediate: true },
    );

    return {
      notificationPosition,
      notificationSkin,
      notificationImage,
      destroy,
    };
  },
);
